import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { BRANDS, getBrandLandingHref } from '../data/brands'
import { listDashboardNewsPosts } from '../lib/news'
import { DEFAULT_OG_IMAGE, INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBreadcrumbSchema } from '../lib/seo'

export const Route = createFileRoute('/merek/')({
  loader: () => listDashboardNewsPosts().catch(() => []),
  head: () => {
    const title = brandTitle('Grosir Produk per Merek di Surabaya')
    const description =
      'Cari produk grosir Tally, Polini, Piaoli, Oreo, Lupo, Haode, Winteku, COMET, dan merek lain di Dropify Surabaya. Cek stok dan harga via WhatsApp.'

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/merek') },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/merek') }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBreadcrumbSchema([
            { name: 'Beranda', path: '/' },
            { name: 'Merek', path: '/merek' },
          ])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            '@id': `${absoluteUrl('/merek')}#brands`,
            name: 'Merek yang tersedia di Dropify',
            itemListElement: BRANDS.map((brand, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: brand.name,
              url: absoluteUrl(getBrandLandingHref(brand)),
            })),
          }),
        },
      ],
    }
  },
  component: BrandIndexPage,
})

function BrandIndexPage() {
  const newsPosts = Route.useLoaderData()
  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <HeaderMiddle />
      <Navbar />

      <div className="bg-slate-900 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
            <a href="/" className="hover:text-white transition-colors">Beranda</a>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            <span className="text-white font-medium">Merek</span>
          </nav>
          <h1 className="text-4xl font-black text-white uppercase tracking-tight mb-2">Grosir per Merek</h1>
          <p className="text-slate-300 text-sm max-w-2xl">
            Pilih merek untuk melihat kategori, model, minimum order, dan cara memesan. Kirim produk, jumlah, serta kota tujuan melalui WhatsApp untuk meminta pemeriksaan stok dan quotation.
          </p>
        </div>
      </div>

      <WhatsAppChannelSection />

      <main>
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {BRANDS.map((brand) => (
            <a
              key={brand.slug}
              href={getBrandLandingHref(brand)}
              className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:border-blue-200 hover:shadow-lg"
            >
              <h2 className="text-xl font-black tracking-tight text-slate-900 group-hover:text-blue-600 transition-colors">
                {brand.name}
              </h2>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">{brand.intro}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600">
                Lihat produk {brand.name}
                <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          ))}
        </div>
      </div>
      <AdNewsProofSection posts={newsPosts} />
      </main>

      <Footer />
    </div>
  )
}
